import { createRoute, Link } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createSignal, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import {
  ArrowRightLeft,
  Check,
  CheckCircle2,
  Copy,
  Loader2,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "@fleet/icons";
import { Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { QrCode } from "@/components/ui/qr-code";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import {
  buildSnapshot,
  buildTransferCode,
  generateTransferKeyBytes,
  importTransferKey,
  SENDER_INITIAL,
  type SenderEvent,
  senderReducer,
  sendPayloadOverChannel,
} from "@aula/shared";
import { connectAsSender, type RtcPeerHandle } from "@/features/p2p-transfer/webrtc";
import {
  createP2pSignalingClient,
  mapTransferError,
  toSnapshotDb,
} from "@/features/p2p-transfer/support";
import { TransferErrorCard, TransferProgressBar } from "@/features/p2p-transfer/transfer-ui";

/**
 * SENDER for device-to-device transfer (P2P via QR).
 *
 * Opening the page creates an ephemeral signaling room and a one-time key;
 * the QR (and copyable code) contain `room + key`. The other device opens
 * "Receive data", pastes/scans the code, and the data travels DIRECTLY through
 * an encrypted WebRTC DataChannel: the server only relays opaque signaling
 * envelopes and can never read the content. Works for guests and accounts:
 * the active user's local snapshot is transferred.
 */
function TransferSendPage() {
  const { t } = useI18n();
  const session = useSession();
  const powerSync = usePowerSyncContext();

  const [state, setState] = createSignal(SENDER_INITIAL);
  const dispatch = (event: SenderEvent) => setState((s) => senderReducer(s, event));
  const [remainingSecs, setRemainingSecs] = createSignal(0);
  const [copied, setCopied] = createSignal(false);

  let peer: RtcPeerHandle | undefined;
  let countdown: ReturnType<typeof setInterval> | undefined;
  const signaling = createP2pSignalingClient();

  const stopCountdown = () => {
    if (countdown !== undefined) {
      clearInterval(countdown);
      countdown = undefined;
    }
  };

  const cleanup = () => {
    stopCountdown();
    peer?.close();
    peer = undefined;
  };

  const start = () => {
    cleanup();
    dispatch({ type: "RESET" });
    dispatch({ type: "CREATE" });
    void (async () => {
      try {
        const room = await signaling.createRoom();
        const keyBytes = generateTransferKeyBytes();
        const key = await importTransferKey(keyBytes);
        const transferCode = buildTransferCode(room.code, keyBytes);
        dispatch({
          type: "ROOM_CREATED",
          code: room.code,
          transferCode,
          expiresInSecs: room.ttlSecs,
        });

        setRemainingSecs(room.ttlSecs);
        countdown = setInterval(() => {
          setRemainingSecs((s) => {
            if (s <= 1) {
              // Room expired without a receiver: stop and make it clear.
              if (state().phase === "waiting-peer") {
                dispatch({ type: "FAIL", kind: "expired" });
                cleanup();
              }
              return 0;
            }
            return s - 1;
          });
        }, 1000);

        peer = connectAsSender({
          signaling,
          code: room.code,
          senderToken: room.senderToken,
          key,
          timeoutMs: room.ttlSecs * 1000,
          onPeerJoined: () => {
            stopCountdown();
            dispatch({ type: "PEER_JOINED" });
          },
        });
        const channel = await peer.channelPromise;
        dispatch({ type: "CHANNEL_OPEN" });

        const db = powerSync.db();
        if (!db) {
          throw new Error("local database unavailable");
        }
        // An empty snapshot is also sent: the flow ends as "completed"
        // (with 0 imported) instead of an ambiguous state.
        const snapshot = await buildSnapshot(toSnapshotDb(db), session.userId());
        const ack = await sendPayloadOverChannel(channel, key, JSON.stringify(snapshot), {
          onProgress: (sent, total) =>
            dispatch({ type: "PROGRESS", transferredBytes: sent, totalBytes: total }),
        });
        dispatch({ type: "ACKED", inserted: ack.inserted, skipped: ack.skipped });
      } catch (error) {
        dispatch({ type: "FAIL", kind: mapTransferError(error) });
      } finally {
        cleanup();
      }
    })();
  };

  onMount(start);
  onCleanup(cleanup);

  const copyCode = () => {
    const current = state();
    if (current.phase !== "waiting-peer") {
      return;
    }
    void (async () => {
      await navigator.clipboard.writeText(current.transferCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    })();
  };

  const percent = () => {
    const current = state();
    if (current.phase !== "sending" || current.progress.totalBytes === 0) {
      return 0;
    }
    return Math.round((current.progress.transferredBytes / current.progress.totalBytes) * 100);
  };

  // Accessors that narrow the type by phase (avoid assertions in the <Match>).
  const waitingState = () => {
    const s = state();
    return s.phase === "waiting-peer" ? s : undefined;
  };
  const doneState = () => {
    const s = state();
    return s.phase === "done" ? s : undefined;
  };
  const errorState = () => {
    const s = state();
    return s.phase === "error" ? s : undefined;
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-8" containerClass="scrollbar-thin">
      <div data-testid="p2p-send-page">
        <PageHeader
          title={t(msg`Transfer to another device`)}
          description={t(msg`Send your data directly to another device, end-to-end encrypted.`)}
          backTo="/profile"
          icon={ArrowRightLeft}
        />
      </div>

      <Card>
        <CardContent class="space-y-6 p-6">
          <div class="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
            <ShieldCheck class="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <p class="text-xs text-muted-foreground">
              {t(
                msg`Your data travels directly between the two devices over an encrypted connection. The one-time key inside the QR code never reaches the server, so nobody in between can read anything.`,
              )}
            </p>
          </div>

          <Switch>
            <Match when={state().phase === "creating" || state().phase === "idle"}>
              <div
                class="flex flex-col items-center gap-3 py-10 text-muted-foreground"
                data-testid="p2p-send-status"
              >
                <Loader2 class="h-8 w-8 animate-spin" />
                <p class="text-sm">{t(msg`Preparing a secure transfer code…`)}</p>
              </div>
            </Match>

            <Match when={waitingState()} keyed>
              {(waiting) => (
                <div class="flex flex-col items-center gap-5" data-testid="p2p-send-status">
                  <QrCode value={waiting.transferCode} size={220} alt={t(msg`Transfer QR code`)} />
                  <div class="w-full space-y-2">
                    <p class="text-center text-sm font-medium">
                      {t(
                        msg`Open "Receive data" on the other device and scan this QR code, or paste the transfer code:`,
                      )}
                    </p>
                    <code
                      class="block w-full select-all break-all rounded-xl border border-border bg-muted/40 p-3 text-center font-mono text-xs"
                      data-testid="p2p-transfer-code"
                    >
                      {waiting.transferCode}
                    </code>
                    <Button
                      variant="outline"
                      class="h-11 w-full gap-2 rounded-xl"
                      onClick={copyCode}
                      data-testid="p2p-copy-code"
                    >
                      <Show when={!copied()} fallback={<Check class="h-4 w-4 text-primary" />}>
                        <Copy class="h-4 w-4" />
                      </Show>
                      <Show when={!copied()} fallback={t(msg`Copied!`)}>
                        {t(msg`Copy transfer code`)}
                      </Show>
                    </Button>
                  </div>
                  <div class="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 class="h-4 w-4 animate-spin" />
                    <span>
                      {/* ICU usa `{var}`, no `{{var}}` (grafía i18next). */}
                      {(() => {
                        const seconds = String(remainingSecs());
                        return t(
                          msg`Waiting for the other device… The code expires in ${seconds}s.`,
                        );
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </Match>

            <Match when={state().phase === "connecting"}>
              <div
                class="flex flex-col items-center gap-3 py-10 text-muted-foreground"
                data-testid="p2p-send-status"
              >
                <Loader2 class="h-8 w-8 animate-spin" />
                <p class="text-sm">{t(msg`Device found. Establishing the direct connection…`)}</p>
              </div>
            </Match>

            <Match when={state().phase === "sending"}>
              <div class="space-y-4 py-6" data-testid="p2p-send-status">
                <p class="text-center text-sm font-medium">
                  {/* ICU usa `{var}`, no `{{var}}` (grafía i18next). */}
                  {(() => {
                    const percentValue = String(percent());
                    return t(msg`Transferring your data… ${percentValue}%`);
                  })()}
                </p>
                <TransferProgressBar percent={percent()} />
              </div>
            </Match>

            <Match when={doneState()} keyed>
              {(done) => (
                <div
                  class="flex flex-col items-center gap-4 py-8 text-center"
                  data-testid="p2p-send-done"
                >
                  <CheckCircle2 class="h-12 w-12 text-primary" />
                  <div class="space-y-1">
                    <p class="text-base font-bold">{t(msg`Transfer complete`)}</p>
                    <p class="text-sm text-muted-foreground">
                      {/* ICU usa `{var}`, no `{{var}}` (grafía i18next). */}
                      {(() => {
                        const inserted = String(done.inserted);
                        const skipped = String(done.skipped);
                        return t(
                          msg`The other device imported ${inserted} items (${skipped} were already there).`,
                        );
                      })()}
                    </p>
                  </div>
                  <Link
                    to="/profile"
                    class="text-sm font-semibold text-primary hover:underline"
                    data-testid="p2p-send-back"
                  >
                    {t(msg`Back to profile`)}
                  </Link>
                </div>
              )}
            </Match>

            <Match when={errorState()} keyed>
              {(failed) => (
                <div class="space-y-4" data-testid="p2p-send-error">
                  <TransferErrorCard kind={failed.kind} icon={XCircle} />
                  <Button
                    variant="outline"
                    class="h-11 w-full gap-2 rounded-xl"
                    onClick={start}
                    data-testid="p2p-send-retry"
                  >
                    <RefreshCw class="h-4 w-4" />
                    {t(msg`Generate a new code`)}
                  </Button>
                </div>
              )}
            </Match>
          </Switch>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export const profileTransferSendRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/transfer-device",
  component: TransferSendPage,
});
